import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import { createInteractivePageSection, updateWidgetProperty, toggleEditMode } from "actions/editor";

import _ from "lodash";
import cx from "classnames";
import { Frame } from 'scenejs';

import { EditorContext } from '../../index';
import StoryboardContext from 'components/Storyboard/context';
import { Container } from '../core/container';
import { Button } from 'react-bootstrap';
import { Background } from 'components/Editor/components/properties/background';

import { Property } from '../../components/properties/';
import Tooltip from "components/Tooltip";
import styles from "./page.module.scss";
import { getBoundingRectFromLayout } from "components/Editor/helper/layoutManager";
import { useChildrenWidgetSelected, useContainerSelector, useHotspotWidgetSelector, useSelectedWidgetSelector } from "components/Editor/hooks/useEditorSession";
import { Widget } from './../../widgets/core';
import Portal from "components/Portal";
import { BackgroundView } from "../background";
import { PAGE, SECTION } from "constants/widgetTypes";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { getMediaInfo } from "actions/pageHealth";

const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

export const PageWidgetContext = React.createContext({
  onResize: () => { }
});

const PageWidgetComponent = function ({ className, style, widgetProps, widgetRef, children }) {
  const ref = widgetRef;

  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;
  const editorWidgetViewer = document.getElementsByClassName('editor_widget_viewer')[0];

  const storyboard = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboard, 'type');
  const background = _.get(widgetProps, 'properties.desktop.background');

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));


  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');

  const childrenSelection = useChildrenWidgetSelected(editor, id);
  const selectedWidget = useSelectedWidgetSelector(editor)?.[0]
  const visibleWitdh = _.get(widgetProps, `properties.${breakpoint}.container.minWidth`);
  const widgetHeight = parseInt(_.get(widgetProps, `properties.${breakpoint}.layout.height`)) || 0;

  const hotspotWidget = useHotspotWidgetSelector(editor);

  const tools = useSelector((state) => _.get(state, `${editor}.present.tools`));

  const offset = breakpoint === 'desktop' ? 100 : 100;

  const [height, setHeight] = useState(`${widgetHeight}px`);

  const showToolbar = useSelector(state => {
    const selected = _.get(state, `${editor}.present.selected`);
    return selected?.length === 0
  });

  const isSelectedWidgetValid = 
    (useContainerSelector(editor, _.get(selectedWidget, "uid"))?.type === PAGE) && 
    (_.get(selectedWidget, "type") !== SECTION);

  const [pageWidgetContext, setPageWidgetContext] = useState({
    onResize: () => { }
  });

  const handleResize = () => {
    var height = 0;
    const parentRect = widgetRef.current.getBoundingClientRect();

    const editorWidgetViewer = document.getElementsByClassName('editor_widget_viewer')[0];

    const widgets = editorWidgetViewer.querySelectorAll(`#${id} .widget[data-widget-id]`);
    Array.from(widgets).forEach(element => {
      const rect = element.getBoundingClientRect();
      height = Math.max(height, rect.bottom - parentRect.top);
    });

    height = Math.max(600, height);
    setHeight(`${height}px`);
  };

  useEffect(() => {
    // Resize the page height widget when the selected widget is deleted
    if(!selectedWidget) {
      handleResize();
    }
  }, [selectedWidget]);

  React.useEffect(() => {
    setPageWidgetContext({
      onResize: handleResize
    });

  }, []);

  React.useEffect(() => {
    const layout = _.get(widgetProps, `properties.${breakpoint}.layout`) || {};
    if (layout && layout.height != height) {
      dispatch(updateWidgetProperty(editor, id, breakpoint, {
        layout: {
          ...layout,
          height: height
        }
      }))
    }
  }, [height]);

  var widgetStyle = {
    ...style,
    width: breakpoint === 'desktop' ? `${visibleWitdh}px` : `${visibleWitdh}px`,
    minHeight: `600px`
  }

  return (
    <>
      <PageWidgetContext.Provider value={pageWidgetContext}>
        <Container widgetProps={widgetProps} widgetRef={widgetRef}>
          <div id={id}
            ref={ref}
            className={cx(className, styles.page, 'cs_page_root', {
              [styles.mobile]: breakpoint === 'mobile'
            })}
            data-storyboard-type={storyboardType}
            data-widget-id={id}
            data-widget-type={type}
            data-container={true}
            data-selectable={false}
            style={widgetStyle}
          >
            {(background && editorWidgetViewer) &&
              <Portal mountNode={editorWidgetViewer}>
                <BackgroundView className={styles.pageBackground} background={background} />
              </Portal>
            }
            {children}

            {hotspotWidget &&
              <Widget key={hotspotWidget.uid} widgetProps={hotspotWidget} />
            }

            {!editorContext.disabled && showToolbar && 
              <PageWidgetDesktopAction editor={editor} breakpoint={breakpoint} {...widgetProps} />
            }
          </div>
        </Container>
      </PageWidgetContext.Provider>

      {!editorContext.disabled && breakpoint === 'desktop' && storyboardType === 'INTERACTIVE_CONTENT' &&
        <>
          <CreatePageButton
            editorSession={editor}
            style={{
              right: `calc((100% - ${visibleWitdh}px)/2)`,
              top: `50%`
            }}
          />
        </>
      }

      {tools.gridlines &&
        <>
          <div className={cx(styles.boundary, styles.top, {[styles.hideBoundary]: childrenSelection.selected && !isSelectedWidgetValid})} style={{
            top: offset
          }} />
          <div className={cx(styles.boundary, styles.bottom, {[styles.hideBoundary]: childrenSelection.selected && !isSelectedWidgetValid})} style={{
            top: `${widgetHeight + offset}px`
          }} />

          <div className={cx(styles.boundary, styles.left, {[styles.hideBoundary]: childrenSelection.selected && !isSelectedWidgetValid})} style={{
            left: `calc((100% - ${visibleWitdh}px)/2)`,
            height: `${widgetHeight + 300}px`
          }} />
          <div id='forGuideLineWidth' style={{width: `calc((100% - ${visibleWitdh}px)/2)`, left: 0, position: "absolute" }}></div>
          <div className={cx(styles.boundary, styles.right, {[styles.hideBoundary]: childrenSelection.selected && !isSelectedWidgetValid})} style={{
            right: `calc((100% - ${visibleWitdh}px)/2)`,
            height: `${widgetHeight + 300}px`
          }} />
        </>
      }

    </>
  );
};

const pageWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'widgetRef', 'children'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

const PageWidget = React.memo(PageWidgetComponent, pageWidgetEquals);

const PageWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isPaletteComponentEditor = location.pathname.includes("paletteComponent")

  const [view, setView] = useState('style')

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const background = _.get(props, 'properties.desktop.background', null);

  const id = _.get(props, 'uid');
  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  if(props.breakpoint === "mobile") {
    return null;
  }
  
  return (
    <div className={styles.widgetAction}>
      {!isPaletteComponentEditor &&
      <Property.Action className={cx(styles.textButton, styles.propertyAction)} onClick={() => changeView('background')}>
        Change Background
      </Property.Action>
      }

      {!editMode && view === 'background' &&
        <Background.Dialog id={id}
          value={background}
          onChange={(value) => {
            dispatch(updateWidgetProperty(editorSession, id, 'desktop', {
              background: value
            }));
          }}
          onCancel={() => {
            closePopup();
          }}
        />
      }
    </div>
  );
}

const CreatePageButton = ({ editorSession, style }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const handleCreate = () => {
    dispatch(createInteractivePageSection(editorSession, 0));
  };

  return (
    <div className={styles.createPageButton} style={style}>
      <Tooltip placement='left' content='Add page'>
        <Button onClick={handleCreate}>
          <i className={cx(styles.icon, "cleverstory-plus")} />
        </Button>
      </Tooltip>
    </div>
  );
};

PageWidget.moveableConfig = (editorSession, widget) => {
  return {
    draggable: false,
    resizable: false,
    rotatable: false,
    selectable: true,
    cloneable: false,
    deletable: false,
    renderDirections: [],
    onResize: (widget, layout) => {
      const frame = new Frame(layout);

      if (frame.has("transform", "translate")) {
        frame.set('transform', 'translate', '0px, 0px');
      }

      const rect = getBoundingRectFromLayout(layout);
      const height = Math.max(600, rect.height);

      frame.set('height', `${height}px`);

      return frame.get();
    }
  };
};

const PageWidgetActions = ({editor, breakpoint, props}) => {

  if(breakpoint === "mobile") {
    return;
  }
  
  return (
    <PageWidgetDesktopAction
      editor={editor}
      breakpoint={breakpoint}
      {...props}
    />
  );
}

PageWidget.sizeConfig = async (widget, mediaCache) => {
  const url = _.get(widget, 'properties.desktop.background.media.url', '');
  if (!url) {
    return {
      size: 0,
      type: 'image'
    };
  }
  return {
    size:  await getMediaInfo(`${IMAGE_CDN_ENDPOINT}/${url}`, mediaCache) || 0, // MB
    type: 'image'
  };
}

PageWidget.processRequest = (widget) => {
  var properties = widget.properties;

  var socialIcon = _.get(properties, 'desktop.social.icon', null);
  if (socialIcon) {
    properties = {
      ...properties,
      desktop: {
        ...properties.desktop,
        social: {
          ...properties.desktop.social,
          icon: socialIcon.id,
        },
      },
    };
  }

  var twitterIcon = _.get(properties, 'desktop.twitter.icon', null);
  if (twitterIcon) {
    properties = {
      ...properties,
      desktop: {
        ...properties.desktop,
        twitter: {
          ...properties.desktop.twitter,
          icon: twitterIcon.id,
        },
      },
    };
  }

  var background = _.get(properties, 'desktop.background');
  var media = _.get(background, 'media');
  if (background && media) {
    properties = {
      ...properties,
      desktop: {
        ...properties.desktop,
        background: {
          ...background,
          media: media.id
        }
      },
    };
  }

  return {
    ...widget,
    properties: properties,
  };
};

export { PageWidget };

